import React from 'react';
import { Alert, Box, Button, Grid, Stack, TextField } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { FProcessStageBar, FWorkbenchShell } from '../workbench';
import HistoricalDataCards from './HistoricalDataCards';
import HistoricalImportGrid from './HistoricalImportGrid';
import HistoricalFactGrid from './HistoricalFactGrid';
import {
  fetchHistoricalCards,
  fetchHistoricalFacts,
  fetchHistoricalImports,
  publishHistoricalImport,
  uploadHistoricalData,
  validateHistoricalImport,
} from './historicalData.apis';

type State = {
  sourcecode: string;
  scenariocode: string;
  datasetcode: string;
  periodcode: string;
  loading: boolean;
  error: string;
  success: string;
  cards: any;
  imports: any[];
  facts: any[];
  importText: string;
};

class HistoricalDataWorkbench extends React.Component<any, State> {
  state: State = {
    sourcecode: 'MANUAL',
    scenariocode: 'BASE2027',
    datasetcode: '',
    periodcode: '',
    loading: false,
    error: '',
    success: '',
    cards: {},
    imports: [],
    facts: [],
    importText:
      '[{"scc":"BASE2027","etc":"SALES","enc":"SUPER","lcc":"ALL","pdc":"2027M01","qty":1,"upr":1,"amt":1}]',
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [cards, imports, facts] = await Promise.all([
        fetchHistoricalCards(),
        fetchHistoricalImports({
          sourcecode: this.state.sourcecode,
          pagesize: 50,
        }),
        fetchHistoricalFacts({
          scenariocode: this.state.scenariocode,
          datasetcode: this.state.datasetcode,
          periodcode: this.state.periodcode,
          pagesize: 100,
        }),
      ]);
      this.setState({ cards, imports, facts });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  upload = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const records = JSON.parse(this.state.importText || '[]');
      await uploadHistoricalData({
        tri: crypto.randomUUID?.() || null,
        soc: this.state.sourcecode,
        scc: this.state.scenariocode,
        rcd: records,
        fln: 'manual_historical_data.json',
        fdt: {},
        att: {},
        exi: { origin: 'HistoricalDataWorkbench' },
        rmk: 'Manual JSON upload',
      });
      this.setState({ success: 'Historical data uploaded.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  validate = async (row: any) => {
    await validateHistoricalImport(row.rid || row.recid || row.importid);
    this.setState({ success: 'Import validated.' });
    await this.load();
  };

  publish = async (row: any) => {
    await publishHistoricalImport(row.rid || row.recid || row.importid);
    this.setState({ success: 'Import published as approved facts.' });
    await this.load();
  };

  filters() {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            size="small"
            label="Source"
            value={this.state.sourcecode}
            onChange={(e) =>
              this.setState({ sourcecode: e.target.value.toUpperCase() })
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            size="small"
            label="Scenario"
            value={this.state.scenariocode}
            onChange={(e) =>
              this.setState({ scenariocode: e.target.value.toUpperCase() })
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            size="small"
            label="Dataset"
            value={this.state.datasetcode}
            onChange={(e) =>
              this.setState({ datasetcode: e.target.value.toUpperCase() })
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            size="small"
            label="Period"
            value={this.state.periodcode}
            onChange={(e) =>
              this.setState({ periodcode: e.target.value.toUpperCase() })
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Button variant="contained" onClick={this.load} fullWidth>
            Apply
          </Button>
        </Grid>
      </Grid>
    );
  }

  uploadPanel() {
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Use this workbench to prepare approved immutable facts. Forecasting
          only consumes published facts.
        </Alert>
        <TextField
          label="Records JSON"
          value={this.state.importText}
          onChange={(e) => this.setState({ importText: e.target.value })}
          multiline
          minRows={4}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={this.upload}
          disabled={this.state.loading}
        >
          Upload Historical Data
        </Button>
      </Stack>
    );
  }

  render() {
    return (
      <FWorkbenchShell
        title="Historical Data Workbench"
        subtitle="Acquire, validate, approve and publish immutable facts for forecasting."
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.load}
        processStage={
          <FProcessStageBar
            stages={
              [
                { code: 'UPLOAD', label: 'Upload', status: 'active' },
                { code: 'VALIDATE', label: 'Validate', status: 'pending' },
                { code: 'PUBLISH', label: 'Publish Facts', status: 'pending' },
                { code: 'FORECAST', label: 'Forecast', status: 'locked' },
              ] as any
            }
          />
        }
        summary={<HistoricalDataCards cards={this.state.cards} />}
        filters={this.filters()}
      >
        <Box>
          <Stack spacing={3}>
            {this.uploadPanel()}
            <HistoricalImportGrid
              rows={this.state.imports}
              loading={this.state.loading}
              onValidate={this.validate}
              onPublish={this.publish}
            />
            <HistoricalFactGrid
              rows={this.state.facts}
              loading={this.state.loading}
            />
          </Stack>
        </Box>
      </FWorkbenchShell>
    );
  }
}
export default HistoricalDataWorkbench;
